import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import getFullUrl from '../../utils/formatUrl';

const GallerySlider = ({ images = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const defaultImages = [
        "https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=1000&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1592280771190-3e2e4d571952?q=80&w=1000&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=1000&auto=format&fit=crop"
    ];

    const displayImages = images.length > 0 ? images : defaultImages;

    const nextSlide = React.useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % displayImages.length);
    }, [displayImages.length]);

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
    };

    // Auto-advance
    useEffect(() => {
        const interval = setInterval(nextSlide, 5000);
        return () => clearInterval(interval);
    }, [nextSlide]);

    return (
        <section id="gallery" className="py-10 bg-slate-900 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex flex-col md:flex-row justify-between items-end mb-6 gap-4">
                    <div>
                        <h4 className="text-amber-400 font-black uppercase tracking-[5px] text-[11px] mb-3">Campus Life</h4>
                        <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tighter">
                            Moments of <span className="text-slate-400">Excellence</span>.
                        </h2>
                    </div>
                    <div className="flex space-x-2">
                        <button 
                            onClick={prevSlide}
                            className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-[#cc0000] transition-all"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button 
                            onClick={nextSlide}
                            className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-[#cc0000] transition-all"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>

                {/* 
                    Mobile: aspect-ratio container so full image shows without cropping
                    Desktop (md+): fixed height cinematic view with object-cover
                */}
                <div className="relative rounded-sm overflow-hidden group aspect-[4/3] md:aspect-auto md:h-[600px]">
                    {displayImages.map((img, idx) => (
                        <div 
                            key={idx}
                            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${idx === currentIndex ? 'opacity-100' : 'opacity-0'}`}
                        >
                            {/* Mobile: object-contain — full image visible, no crop */}
                            <img 
                                src={getFullUrl(img)} 
                                alt={`Campus ${idx + 1}`} 
                                className="w-full h-full object-contain md:object-cover bg-slate-900 md:scale-105 md:group-hover:scale-100 transition-transform duration-[2000ms]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        </div>
                    ))}

                    {/* Indicators — centered on mobile, left-aligned on desktop */}
                    <div className="absolute bottom-4 md:bottom-10 left-1/2 -translate-x-1/2 md:left-10 md:translate-x-0 flex space-x-3 z-20">
                        {displayImages.map((_, idx) => (
                            <button 
                                key={idx}
                                onClick={() => setCurrentIndex(idx)}
                                className={`h-1 transition-all duration-300 ${idx === currentIndex ? 'w-8 md:w-12 bg-amber-400' : 'w-4 bg-white/30 hover:bg-white/50'}`}
                            ></button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default GallerySlider;
